<template>
  <div>
    <slot name="label">
      <h2 class="mb-2">
        {{
          $t(
            "common:common.documents.label",
            "Documents",
            "Label of the documents section"
          )
        }}
      </h2>
    </slot>
    <div
      v-if="$allowedMutation(allowedMutations, 'eventDocumentAdd')"
      class="my-2"
    >
      <v-file-input
        v-model="file"
        :label="
          $t(
            'common:common.uploaddocument.label',
            'Document upload',
            'Label of the document upload section'
          )
        "
        data-test="eventDocumentInput"
        :loading="loading"
        variant="outlined"
        hide-details
        append-inner-icon="$file"
        single-line
      />
      <div class="text-right">
        <v-btn
          :disabled="!Boolean(file)"
          :loading="loading"
          variant="outlined"
          data-test="eventDocumentUpload"
          class="text-primary mt-4"
          @click="uploadFile"
        >
          <v-icon size="small" start> $iconUpload </v-icon>
          <slot name="upload-text">
            {{ $t("events:uploadDocument.button") }}
          </slot>
        </v-btn>
      </div>
    </div>
    <div v-if="documents.length" class="v-col-12">
      <v-divider />
      <table class="entsys--table no-header">
        <tbody>
          <tr v-for="(item, index) in documents" :key="index">
            <td>
              <a
                :href="item.permalink"
                target="_blank"
                class="event-document-filename"
              >
                {{ item.filename }}
              </a>
            </td>
            <td class="text-right">
              <v-tooltip location="bottom">
                <template #activator="{ props }">
                  <v-btn-icon
                    size="small"
                    icon
                    v-bind="props"
                    class="ma-2"
                    @click="copyLink(item)"
                  >
                    <v-icon size="small"> $iconCopy </v-icon>
                  </v-btn-icon>
                </template>
                <span>{{
                  $t("common:button.copyLink.tooltip", "Copy link")
                }}</span>
              </v-tooltip>

              <v-btn-icon
                v-if="$allowedMutation(allowedMutations, 'eventDocumentDelete')"
                icon
                size="small"
                color="remove"
                class="ma-2"
                data-test="eventDocumentDelete"
                :aria-label="$t('common:documentDeleteButton.alt')"
                @click="deleteItem(item.id, $event)"
              >
                <v-icon color="white" size="small"> $iconTrash </v-icon>

                <v-progress-circular
                  v-show="false"
                  width="4"
                  size="18"
                  color="white"
                  indeterminate
                />
              </v-btn-icon>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else-if="!isNoncompetition">
      <h2 class="text-secondary text-center">
        {{
          $t(
            "events:EventPage.DocumentsSection.NoDocuments.message",
            "No documents uploaded"
          )
        }}
      </h2>
    </div>
  </div>
</template>
<script>
import gql from "fraql";
import { showLoadingButton } from "@/utils/showLoadingButton";

export default {
  name: "EventDocumentsTemplate",
  inject: {
    isNoncompetition: {
      default: false
    }
  },
  props: {
    documents: {
      type: Array,
      required: true
    },
    eventId: {
      type: String,
      required: true
    },
    allowedMutations: {
      type: Array,
      required: true
    }
  },
  emits: ["update:documents"],
  data() {
    return {
      file: null,
      loading: false
    };
  },
  methods: {
    async uploadFile() {
      this.loading = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation eventDocumentAdd($eventId: ID!, $document: Upload!) {
              eventDocumentAdd(
                input: { eventId: $eventId, document: $document }
              ) {
                event {
                  id
                  documentsListing {
                    id
                    filename
                    url
                    path
                    permalink
                  }
                }
              }
            }
          `,
          variables: {
            eventId: this.eventId,
            document: this.file
          }
        });
        this.file = null;
        this.$emit(
          "update:documents",
          data.eventDocumentAdd.event.documentsListing
        );
      } catch (err) {
        this.$root.$snackbarServerError(err, { level: "error" });
      } finally {
        this.loading = false;
      }
    },
    async deleteItem(ID, node) {
      const response = await this.$root.$confirm(
        this.$t("events:schedule.documents.delete.modal.heading"),
        this.$t("events:schedule.documents.delete.modal.question"),
        { color: "red" }
      );
      if (!response) return;

      const removeLoading = showLoadingButton(node.target);
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation eventDocumentDelete($eventId: ID!, $documentId: ID!) {
              eventDocumentDelete(
                input: { eventId: $eventId, documentId: $documentId }
              ) {
                event {
                  id
                  documentsListing {
                    id
                    filename
                    path
                    url
                    permalink
                  }
                }
              }
            }
          `,
          variables: { eventId: this.eventId, documentId: ID }
        });

        this.$emit(
          "update:documents",
          data.eventDocumentDelete.event.documentsListing
        );

        removeLoading();
        this.$root.$snackbar("success", {
          text: this.$t(
            "common:feedbackMessage.removedSuccessfully.text",
            "Succesfully deleted!"
          )
        });
      } catch (err) {
        this.$root.$snackbarServerError(err, { level: "error" });
      } finally {
        removeLoading();
      }
    },
    copyLink(doc) {
      navigator.clipboard.writeText(doc.permalink);
    }
  },
  fragments: {
    eventDocumentFields: gql`
      fragment _ on EventDocument {
        id
        url
        path
        filename
        permalink
      }
    `
  }
};
</script>
<style lang="scss">
.event-document-filename {
  word-break: break-all;
  font-weight: bold;

  @media screen and (min-width: 1026px) {
    font-size: large;
  }
}
</style>
